import * as React from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../primitives/alert-dialog';
import { buttonVariants } from '../primitives/button';
import { Input } from '../primitives/input';
import { Label } from '../primitives/label';
import { cn } from '../lib/utils';

/**
 * Destructive-verb confirmation dialog for plant-floor commands.
 *
 * Composes the `AlertDialog` primitive into the domain shape every "are you
 * sure you want to Abort/Stop/Disconnect this machine?" flow needs: a verb-led
 * title, the affected equipment + its current state in the body, an optional
 * typed reason field (regulated tenants gate the confirm button on it), and an
 * optional e-signature slot so GxP deployments can mount their own signature
 * capture inside the dialog without this package depending on any compliance
 * module.
 *
 * The dialog is deliberately controlled-only (`open` + `onConfirm`/`onCancel`)
 * so an imperative promise-based wrapper (e.g. a module-local `useConfirm`
 * hook) can drive it: open → operator confirms or cancels → exactly one
 * callback fires.
 */

/** What the operator submitted when they pressed the confirm verb. */
export interface ConfirmActionDetail {
  /** The typed reason (empty string when the field was hidden or left blank). */
  reason: string;
}

/** Visibility / gating of the typed reason field. */
export type ConfirmReasonMode = 'hidden' | 'optional' | 'required';

export interface ConfirmActionDialogProps {
  /** Controlled open state. */
  open: boolean;
  /** Dialog heading, e.g. "Abort Filler 02?". */
  title: React.ReactNode;
  /** Optional free-form body copy under the title. */
  description?: React.ReactNode;
  /** Name of the equipment / device the verb targets (shown in the body). */
  equipmentName?: string;
  /** Current PackML / lifecycle state of the target (shown in the body). */
  currentState?: string;
  /** The action verb the confirm button carries, e.g. "Abort". */
  confirmVerb: string;
  /** Render the confirm button with destructive styling. Defaults to true. */
  destructive?: boolean;
  /**
   * Typed reason field: `hidden` (default) renders no field, `optional`
   * renders it without gating, `required` disables the confirm button until a
   * non-blank reason is typed.
   */
  reason?: ConfirmReasonMode;
  /** Label for the reason field. */
  reasonLabel?: string;
  /** Placeholder for the reason field. */
  reasonPlaceholder?: string;
  /**
   * Optional e-signature slot rendered between the body and the footer. GxP
   * tenants mount their signature-capture UI here; a render-prop receives the
   * current reason text so the signature record can bind to it.
   */
  eSignatureSlot?: React.ReactNode | ((ctx: { reason: string }) => React.ReactNode);
  /** Fired once when the operator presses the confirm verb. */
  onConfirm: (detail: ConfirmActionDetail) => void;
  /** Fired once when the operator cancels (button, Escape, or overlay). */
  onCancel: () => void;
}

export function ConfirmActionDialog({
  open,
  title,
  description,
  equipmentName,
  currentState,
  confirmVerb,
  destructive = true,
  reason = 'hidden',
  reasonLabel = 'Reason',
  reasonPlaceholder = 'Why is this action needed?',
  eSignatureSlot,
  onConfirm,
  onCancel,
}: ConfirmActionDialogProps) {
  const [reasonText, setReasonText] = React.useState('');

  // Reset the typed reason whenever a new confirmation opens so a prior
  // request's justification can never leak into the next one. The settled flag
  // guarantees exactly one of onConfirm/onCancel fires per open cycle: Radix
  // closes the dialog after the Action/Cancel buttons run, which would
  // otherwise re-enter through onOpenChange(false) and fire onCancel a second
  // time after a confirm.
  const settledRef = React.useRef(false);
  React.useEffect(() => {
    if (open) {
      settledRef.current = false;
      setReasonText('');
    }
  }, [open]);

  const settleOnce = (fire: () => void) => {
    if (settledRef.current) return;
    settledRef.current = true;
    fire();
  };

  const reasonSatisfied = reason !== 'required' || reasonText.trim().length > 0;
  const reasonFieldId = React.useId();

  const signature =
    typeof eSignatureSlot === 'function' ? eSignatureSlot({ reason: reasonText }) : eSignatureSlot;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) settleOnce(onCancel);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>

        {equipmentName || currentState ? (
          <dl className="grid grid-cols-2 gap-3 rounded-md border p-3 text-sm">
            {equipmentName ? (
              <div className="min-w-0">
                <dt className="text-xs text-muted-foreground">Equipment</dt>
                <dd className="truncate font-medium">{equipmentName}</dd>
              </div>
            ) : null}
            {currentState ? (
              <div className="min-w-0">
                <dt className="text-xs text-muted-foreground">Current state</dt>
                <dd className="truncate font-mono">{currentState}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {reason !== 'hidden' ? (
          <div className="space-y-1.5">
            <Label htmlFor={reasonFieldId} className="text-xs">
              {reasonLabel}
              {reason === 'required' ? (
                <span aria-hidden className="ml-0.5 text-destructive">
                  *
                </span>
              ) : null}
            </Label>
            <Input
              id={reasonFieldId}
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder={reasonPlaceholder}
              aria-required={reason === 'required'}
            />
          </div>
        ) : null}

        {signature ? <div data-slot="e-signature">{signature}</div> : null}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => settleOnce(onCancel)}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={cn(destructive && buttonVariants({ variant: 'destructive' }))}
            disabled={!reasonSatisfied}
            onClick={() => settleOnce(() => onConfirm({ reason: reasonText.trim() }))}
          >
            {confirmVerb}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
